/**
 * Iter-185 — Report Photo Captions.
 *
 * Photos on an inspection are plain data-URI strings held in several arrays
 * (general photos, per-failure photos, replacement photos, custom-question
 * photos). Captions live in ONE map on the inspection —
 * `photo_captions: { [photoKey]: caption }` — keyed by a short hash of the
 * photo itself, so a caption follows its photo through reordering, removal
 * and whichever strip it sits in. Same hash on every platform (pure JS).
 */

/** djb2 over the string, mixed with the length. Fast enough for data URIs. */
export const photoKey = (uri: string): string => {
  let h = 5381;
  for (let i = 0; i < uri.length; i++) {
    h = ((h << 5) + h + uri.charCodeAt(i)) | 0; // h * 33 + c
  }
  return `p${(h >>> 0).toString(36)}_${uri.length.toString(36)}`;
};

export type PhotoCaptions = Record<string, string>;

export const captionFor = (captions: PhotoCaptions | null | undefined, uri: string): string => {
  if (!captions || !uri) return '';
  const c = captions[photoKey(uri)];
  return typeof c === 'string' ? c.trim() : '';
};

/** Returns a new map with the caption set (or removed when empty). */
export const withCaption = (captions: PhotoCaptions | null | undefined, uri: string, caption: string): PhotoCaptions => {
  const next: PhotoCaptions = { ...(captions || {}) };
  const k = photoKey(uri);
  const text = (caption || '').trim();
  if (text) next[k] = text.slice(0, 200);
  else delete next[k];
  return next;
};

/** Drop captions whose photo no longer exists anywhere on the inspection. */
export const pruneCaptions = (captions: PhotoCaptions | null | undefined, allPhotos: string[]): PhotoCaptions => {
  if (!captions) return {};
  const keep = new Set(allPhotos.filter(Boolean).map(photoKey));
  const next: PhotoCaptions = {};
  for (const [k, v] of Object.entries(captions)) if (keep.has(k) && v) next[k] = v;
  return next;
};
